import { Head, Link, useForm } from "@inertiajs/react"
import { ArrowLeft } from "lucide-react"
import { useEffect } from "react"
import { CoachLayout } from "../components/CoachLayout"
import { Button } from "@/components/ui/button"
import { FileUploadField } from "@/components/ui/file-upload-field"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

// Coach-side Edit form. Same field shape as New: only the fields a coach
// actually controls (title, description, status, cover image). Category,
// visibility, and public_status are pinned server-side.
export default function CourseEdit({
  currentUser,
  course,
}) {
  const form = useForm({
    title: course?.title || "",
    description: course?.description || "",
    status: course?.status || "draft",
    cover_image: null,
    _purge_cover_image: false,
  })
  const { transform } = form

  // See Admin/Courses/Show.jsx — a null file field serializes to "" under
  // forceFormData, and Active Storage reads that as an explicit delete
  // (task #524). Only send the key when it carries a real File.
  useEffect(() => {
    transform((data) => {
      if (data.cover_image instanceof File) return data
      const { cover_image, ...rest } = data
      return rest
    })
  }, [transform])

  const handleSubmit = (e) => {
    e.preventDefault()
    form.patch(`/app/coach/courses/${course.id}`, {
      forceFormData: true,
    })
  }

  return (
    <CoachLayout currentUser={currentUser}>
      <Head title={`Edit ${course.title} - Kingdom Factor`} />

      <div className="max-w-2xl mx-auto space-y-6">
        <Link
          href={`/app/coach/courses/${course.id}`}
          className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-700 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Course
        </Link>

        <Card>
          <CardHeader>
            <CardTitle className="font-display text-xl">Edit Course</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="course-title">
                  Title <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="course-title"
                  placeholder="Course title"
                  value={form.data.title}
                  onChange={(e) => form.setData("title", e.target.value)}
                  required
                />
                {form.errors.title && (
                  <p className="text-sm text-red-600">{form.errors.title}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="course-description">Description</Label>
                <Textarea
                  id="course-description"
                  placeholder="Optional description..."
                  className="min-h-[100px]"
                  value={form.data.description}
                  onChange={(e) => form.setData("description", e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={form.data.status}
                  onValueChange={(v) => form.setData("status", v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
                {/* The model rejects publishing a course with no published
                    lesson and attaches the error to :status — surface it. */}
                {form.errors.status && (
                  <p className="text-sm text-red-600">{form.errors.status}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="course-cover-image">Cover Image</Label>
                <FileUploadField
                  id="course-cover-image"
                  accept="image/webp,image/png,image/jpeg"
                  value={form.data.cover_image}
                  onChange={(f) => form.setData("cover_image", f)}
                  previewSrc={course.coverImageUrl}
                  existingFileUrl={course.coverImageUrl}
                  pendingRemoval={form.data._purge_cover_image}
                  onRequestRemoveExisting={() =>
                    form.setData("_purge_cover_image", true)
                  }
                  onUndoRemoveExisting={() =>
                    form.setData("_purge_cover_image", false)
                  }
                  helpText="WebP, PNG, or JPEG. Max 5 MB."
                />
                {form.errors.cover_image && (
                  <p className="text-sm text-red-600">
                    {form.errors.cover_image}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2 pt-4">
                <Button asChild variant="outline" type="button">
                  <Link href={`/app/coach/courses/${course.id}`}>Cancel</Link>
                </Button>
                <Button
                  type="submit"
                  variant="gradient"
                  disabled={form.processing}
                >
                  {form.processing ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </CoachLayout>
  )
}
